/**
 * Web's SSE pipeline. Single instance per page — `useNotificationsStream`
 * mounts the lifecycle once at the top of App (gated on `paired === true`)
 * so it survives MainPage ↔ SettingsPage navigation, and tears it down on
 * unmount.
 *
 * Roughly the web equivalent of apps/extension/src/background/index.ts's
 * SSE wiring, minus the persistent service-worker concerns:
 *
 *   - Open SSE on start; persist `sseConnected` so the UI can show a
 *     dot. Subscribe to incoming `notification` and `dismiss` events.
 *   - On disconnect / on tab becoming visible, run `/sync` catch-up
 *     and re-open the stream. The extension uses `chrome.alarms` for
 *     a 60s tick; the web uses `visibilitychange` + a foreground
 *     setInterval (no alarm minimum-period clamp here).
 *   - On `pairingLost` — the server rejected the connection because
 *     the device was unpaired remotely — wipe credentials so the app
 *     re-routes to the pairing screen.
 *
 * Backgrounded delivery (Web Push, service worker hookup, badge
 * updates, install prompt) is the next chunk of work and lands in a
 * separate phase.
 *
 * TODO(test-debt): event-handler paths (`handlePairingLost`,
 * `dropFromStorage` routing) have unit coverage in
 * `sse.test.ts`. The remaining untested lifecycle concerns — the
 * 30 s `setInterval`, the `visibilitychange` listener, the in-flight
 * reconnect Promise lock, and the `stopped` guard threaded through
 * the sync loop — still need harness work (mocking SSEClient +
 * EventSource + document.visibilityState end-to-end). Tracked
 * separately; should land before any major SSE pipeline changes.
 */

import { SSEClient, type SSEEvent } from "@reflecto/client-core";
import type { SyncMessage } from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import { syncMessages } from "../api/client";
import { API_BASE } from "../api/config";
import { logError, logWarn } from "../lib/log";
import { trackPairingLost } from "./analytics";
import { conversations } from "./conversationsStore";
import { processIncomingMessage } from "./notifications";
import { dropNotificationFromStorage } from "./notificationsStore";
import { handlePairingLostTransition } from "./pairingLifecycle";

const RECONNECT_TICK_MS = 30_000;

/**
 * How long an SSE connection that still reports itself connected may go silent
 * before the tick treats it as dead. The server writes a `: ping` comment
 * every 30s (`apps/server/src/sse.ts`), so this is three missed pings.
 *
 * `isConnected` alone is not a health signal: it goes false only when the read
 * loop EXITS, and a loop stalled on a half-open socket (laptop sleep, network
 * change — no FIN is delivered and `fetch` has no read timeout) has not
 * exited. Without this, the 30s tick below sees a "connected" client forever
 * and the tab silently stops receiving until it is reloaded. Same threshold
 * and reasoning as the extension's `SSE_STALE_MS`.
 */
const SSE_STALE_MS = 90_000;

export interface SSELifecycle {
  /** Tear down the SSE connection and stop the reconnect tick. */
  stop: () => void;
}

/**
 * Open the SSE connection and the reconnect tick. Returns the
 * teardown handle. Call `stop()` on unmount.
 *
 * Designed to be invoked exactly once per page mount — running it
 * twice would race two SSEClient instances writing the same
 * `sseConnected` flag and processing the same `/sync` results twice.
 */
export function startNotificationsStream(): SSELifecycle {
  const client = new SSEClient();
  let reconnectTimer: ReturnType<typeof setInterval> | null = null;
  let visibilityListener: (() => void) | null = null;
  let stopped = false;
  // In-flight reconnect lock. The setInterval tick (30s) and the
  // visibilitychange listener can both fire within the same event
  // loop turn — without this guard, both would pass the synchronous
  // `client.isConnected` check and BOTH would call syncMessages +
  // client.connect, leaking one EventSource and replaying the same
  // missed array twice. The lock is held across the entire async
  // body so any subsequent invocation while one is in flight is a
  // no-op until the current attempt fully resolves.
  let reconnecting: Promise<void> | null = null;
  // pairing-lost idempotency. Two paths can fire it: the SSE
  // `disconnect` event handler below (server pushed an explicit
  // disconnect) and SSEClient's `onPairingLost` callback (server
  // returned 401/403/404 on the connect handshake). They normally
  // can't overlap on the same connection, but a stray race during
  // reconnect makes a guard cheap insurance — the storage writes
  // are idempotent, but skipping the second call avoids wasted I/O.
  // The flag stays latched only when the transition actually WIPED;
  // a stale-skipped signal resets it inside handlePairingLost so the
  // surviving lifecycle (which reconnects with the newer token) can
  // still report status and act on a future genuine loss.
  let pairingLostFired = false;
  // Token this connection is opened with — captured immediately
  // before the `client.connect()` call below, and re-captured on
  // every subsequent reconnect. `handlePairingLost` passes it to
  // `handlePairingLostTransition` (services/pairingLifecycle.ts),
  // which compares it against the CURRENT storage token before
  // wiping: a mismatch means a newer pairing has already superseded
  // this connection, so the wipe must be skipped rather than
  // destroying the new pairing's credentials. See that module's
  // docblock for the full race + guard rationale.
  let connectionToken: string | undefined;

  const handleEvent = async (event: SSEEvent): Promise<void> => {
    switch (event.event) {
      case "notification": {
        let msg: SyncMessage;
        try {
          msg = JSON.parse(event.data);
        } catch (err) {
          logWarn("sse", "notification_event_parse_failed", err);
          return;
        }
        await processIncomingMessage(msg);
        break;
      }
      case "dismiss": {
        // Server pushes `dismiss` when the paired Android dismisses.
        // Drop the matching card from local state.
        let parsed: { msgId?: string };
        try {
          parsed = JSON.parse(event.data);
        } catch (err) {
          logWarn("sse", "dismiss_event_parse_failed", err);
          return;
        }
        if (!parsed.msgId) return;
        // Catch IndexedDB failures so a single transient storage
        // error doesn't reject the whole SSE event handler. Without
        // this guard the rejection bubbles to onEvent's caller —
        // SSEClient treats onEvent as fire-and-forget — and the
        // dismissed card silently stays in the UI even though
        // Android already cleared it on the phone.
        try {
          await dropFromStorage(parsed.msgId);
        } catch (err) {
          logError("sse", "drop_from_storage_failed", err);
        }
        break;
      }
      case "disconnect": {
        // handlePairingLost BEFORE client.disconnect: after a genuine
        // wipe `pairingLostFired` is still latched, so when
        // client.disconnect subsequently fires `onStatusChange(false)`
        // the callback's guard suppresses the write into the freshly
        // cleaned store. After a stale skip the flag has been reset
        // and the `sseConnected: false` write goes through — correct,
        // since this connection IS closed until the reconnect tick
        // re-opens with the newer token. Same swallow-and-log
        // treatment as the dismiss branch — the multi-step storage
        // wipe can throw partway; logging here makes the failure
        // visible in dev-tools. pairingLostFired prevents re-entry
        // while the transition is in flight.
        try {
          await handlePairingLost("server_disconnect");
        } catch (err) {
          logError("sse", "handle_pairing_lost_failed", err);
        }
        client.disconnect();
        break;
      }
    }
  };

  const handlePairingLost = async (
    trigger: "handshake_refused" | "server_disconnect"
  ): Promise<void> => {
    if (pairingLostFired) return;
    pairingLostFired = true;
    try {
      // The transition itself — including the token-identity guard
      // that skips the wipe when a newer pairing has already
      // superseded this connection — lives in
      // services/pairingLifecycle.ts so there is exactly one place
      // that decides whether a server-initiated pairing-loss signal
      // is safe to act on. See that module's docblock for the full
      // race + guard rationale and the result contract.
      const result = await handlePairingLostTransition(connectionToken);
      // Report only a transition that actually destroyed something. A
      // "stale_skipped" result means the token guard found a NEWER pairing
      // and left storage untouched, so counting it would report a loss that
      // never happened. A throw leaves the guard reset and a follow-up
      // signal retries the wipe — the event fires on that attempt instead,
      // which also keeps the retry from double-counting.
      //
      // Placement differs from the extension deliberately: its
      // `handlePairingLost` has no stale guard and always wipes, so it can
      // report up front; here the wipe is conditional, so the report is too.
      if (result !== "stale_skipped") trackPairingLost(trigger);
      if (result === "stale_skipped") {
        // Stale signal — storage still holds the NEWER pairing, so
        // `paired` stays true, this lifecycle is NOT torn down, and
        // the 30s reconnect tick re-opens the stream with the newer
        // token. The flag must not stay latched on that surviving
        // lifecycle: latched, it would suppress every future
        // onStatusChange write (freezing `sseConnected`) and — far
        // worse — make a LATER GENUINE pairing loss on the new
        // connection return early above, leaving `paired: true`
        // with dead credentials until a reload. On "wiped" the flag
        // stays latched exactly as before (the App unmounts this
        // lifecycle once `paired` flips false). The double-fire
        // idempotency the flag exists for is preserved: a genuine
        // wipe never resets it, and a second near-simultaneous
        // stale signal just re-skips harmlessly via the token
        // guard.
        pairingLostFired = false;
      }
    } catch (err) {
      // Reset the guard on failure so a follow-up event (next SSE
      // disconnect, next reconnect-time 401) can retry the wipe.
      // Otherwise a transient IndexedDB failure could leave the user
      // stranded — though the set-first ordering means even a
      // mid-wipe failure leaves the app in a recoverable state
      // (pairing screen on reload).
      pairingLostFired = false;
      throw err;
    }
  };

  const reconnect = async (): Promise<void> => {
    if (stopped) return;
    if (reconnecting) return reconnecting;
    // A stream that has gone silent past SSE_STALE_MS is dead even though it
    // still reports itself connected — tear it down and re-open. `connect()`
    // aborts the old attempt itself, but disconnecting first also drops the
    // `sseConnected` flag so the UI isn't claiming a live connection through
    // the reconnect.
    if (client.isConnected) {
      if (!client.isStale(SSE_STALE_MS)) return;
      logWarn("sse", "stale_stream_reconnect");
      client.disconnect();
    }
    reconnecting = (async () => {
      try {
        const { paired, token } = await webStorage.get("paired", "token");
        if (!paired || !token || stopped) return;

        // Catch up on missed messages first — mirrors the extension's
        // handleSSEReconnect ordering. Failures are non-fatal: we still
        // try to re-open the stream so the user gets new messages even
        // if /sync was transiently unavailable.
        try {
          const { lastSeqId } = await webStorage.get("lastSeqId");
          const missed = await syncMessages(token, lastSeqId ?? 0);
          for (const msg of missed) {
            // Re-check `stopped` inside the loop. A torn-down lifecycle
            // (useEffect cleanup, StrictMode dev double-mount) must not
            // keep writing to storage / advancing lastSeqId after stop —
            // that would race the new lifecycle's own /sync and produce
            // duplicates or skipped messages.
            if (stopped) return;
            await processIncomingMessage(msg);
          }
        } catch (err) {
          // WARN, not ERROR: `logError` forwards to Sentry (lib/log.ts) and this
          // block's failures are conditions of the moment, not defects. A device
          // that drops off the network mid-reconnect throws `TypeError: Failed to
          // fetch` here; a 5xx or an expired token throws a plain Error. Both are
          // already declared non-fatal three lines up — we open the stream anyway
          // and the next reconnect retries the catch-up — so reporting them filed
          // every offline reconnect as a production error (PWA-2).
          //
          // No defect signal is lost by the downgrade: `processIncomingMessage`
          // catches its own failures and reports them itself under
          // `notifications/process_incoming_failed`, so a decrypt or storage bug in
          // the loop below never propagates to this catch in the first place.
          logWarn("sse", "reconnect_sync_failed", err);
        }

        if (stopped) return;
        const { lastSeqId } = await webStorage.get("lastSeqId");
        // Capture the token this connection is about to open with —
        // see the `connectionToken` declaration above for why.
        connectionToken = token;
        client.connect({
          url: API_BASE,
          token,
          lastEventId: lastSeqId ?? 0,
          onEvent: handleEvent,
          onPairingLost: () => {
            void handlePairingLost("handshake_refused");
          },
          onStatusChange: (connected) => {
            // Skip the write when a pairing-lost wipe has already
            // landed. Two paths hit this: the React unmount → stop()
            // → client.disconnect() chain (which runs after
            // handlePairingLost has cleared storage), and the
            // disconnect-SSE-event handler above (which now invokes
            // handlePairingLost first, so the flag is set by the
            // time client.disconnect fires this callback).
            if (!pairingLostFired) {
              void webStorage.set({ sseConnected: connected });
            }
          },
          onError: (err) => {
            logWarn("sse", "transport_error", err);
          },
        });
      } finally {
        reconnecting = null;
      }
    })();
    return reconnecting;
  };

  // Initial connect.
  void reconnect();

  // Periodic health check while the page is alive.
  reconnectTimer = setInterval(() => {
    void reconnect();
  }, RECONNECT_TICK_MS);

  // Visibility listener — when the tab comes back into focus, run
  // /sync immediately rather than waiting up to RECONNECT_TICK_MS.
  visibilityListener = () => {
    if (document.visibilityState === "visible") {
      void reconnect();
    }
  };
  document.addEventListener("visibilitychange", visibilityListener);

  return {
    stop: () => {
      stopped = true;
      if (reconnectTimer) clearInterval(reconnectTimer);
      if (visibilityListener) {
        document.removeEventListener("visibilitychange", visibilityListener);
      }
      client.disconnect();
      // Skip the sseConnected write when handlePairingLost already wiped
      // storage — otherwise this re-inserts an orphan key into an
      // otherwise-clean store.
      if (!pairingLostFired) {
        void webStorage.set({ sseConnected: false });
      }
    },
  };
}

/**
 * Drop a message from local storage when the paired Android dismisses
 * it (server-pushed `dismiss` event). Hits the flat notifications
 * array AND — via the conversations module — any conversation thread
 * that contains the msgId. Routing through `conversations.removeMessageByMsgId`
 * keeps `seenConversationHashes`, `unreadCount`, and the in-memory
 * msgIdIndex all in sync; a direct mutation of `conversations` storage
 * would orphan hash entries and leave the reverse index stale.
 */
async function dropFromStorage(msgId: string): Promise<void> {
  // Try the flat notifications array first via the shared helper —
  // same logic used by user-initiated dismiss in `messages.ts`, so
  // unread-count math + the not-found short-circuit stay defined
  // in one place.
  const dropped = await dropNotificationFromStorage(msgId);
  if (dropped) return;
  // Not in the flat list — try conversation threads. removeMessageByMsgId
  // returns false if no thread contained it (e.g. server raced ahead of
  // local state, or the message was already dropped client-side).
  await conversations.removeMessageByMsgId(msgId);
}
